import { Component, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AlarmService } from '../../../../services/alarm.service';
import { LogService } from '../../../../services/log.service';
import { DialogBase } from '../../../../shared/components/dialogs/dialog-base/dialog-base';
import { TableComponent } from '../../../../shared/components/table/table';
import { TranslateSafePipe } from '../../../../shared/pipes/translation.pipe';
import { formatTimeAgoTranslation } from '../../../../shared/utils/date.util';
import { AlarmDto, AlarmFindAndCountResponseDto, AlarmHistoryRow, AlarmRow } from '../../../../types/dtos/alarm.dto';
import { DeviceDto } from '../../../../types/dtos/device.dto';
import { LoggingActionEnum, LoggingTargetEnum } from '../../../../types/enums/logging.enum';
import { TableConfig, TableRowDto } from '../../../../types/interfaces/table.interface';
import { DialogAlarmDetail } from '../../../alarms/dialog-alarm-detail/dialog-alarm-detail';
import { DashboardBoxComponent } from '../../../dashboard/dashboard-box/dashboard-box.component';

@Component({
  selector: 'app-device-detail-alarm-history',
  imports: [
    DashboardBoxComponent,
    DialogAlarmDetail,
    DialogBase,
    TableComponent,
    TranslateSafePipe,
  ],
  templateUrl: './device-detail-alarm-history.html',
  styleUrl: './device-detail-alarm-history.css',
})
export class DeviceDetailAlarmHistory {
  public selectedAlarm: AlarmDto | null = null;
  public isAlarmDetailOpen: boolean = false;
  public isAlarmStatusUpdateOpen: boolean = false;
  public alarmStatusLoading: boolean = false;

  public alarmTableRows: TableRowDto<AlarmHistoryRow>[] = [];

  constructor(
    private readonly _alarmService: AlarmService,
    private readonly _logService: LogService,
    private readonly _translateService: TranslateService,
  ) {
  }

  private _device: DeviceDto | null = null;

  public alarmTableConfig: TableConfig<AlarmRow> = {
    columns: [
      {
        key: 'status',
        title: 'common.table.status.label',
        isTranslated: true,
        isValueTranslated: true,
        isTag: true,
        overrideValue: (row: AlarmRow) => 'common.enum.alarm-status.' + row.status,
        getTagColor: (row: AlarmRow) => this._alarmService.mapAlarmStatusClass(row.status),
        sticky: 'left',
      },
      {
        key: 'severity',
        title: 'common.table.severity.label',
        isTranslated: true,
        isValueTranslated: true,
        isTag: true,
        overrideValue: (row: AlarmRow) => 'common.enum.alarm-severity.' + row.severity,
        getTagColor: (row: AlarmRow) => this._alarmService.mapAlarmSeverityClass(row.severity),
      },
      {
        key: 'message',
        title: 'common.table.message.label',
        isTranslated: true,
      },
      {
        key: 'created_at',
        title: 'common.table.created-at.label',
        isTranslated: true,
        isValueTranslated: true,
        overrideValue: (row: AlarmRow) => formatTimeAgoTranslation(this._translateService, row.created_at),
        sticky: 'right',
      },
    ],
    mobile: {
      tags: [
        {
          key: 'status',
          isValueTranslated: true,
          overrideValue: (row: AlarmRow) => 'common.enum.alarm-status.' + row.status,
          getColor: (row: AlarmRow) => this._alarmService.mapAlarmStatusClass(row.status),
        },
        {
          key: 'severity',
          isValueTranslated: true,
          overrideValue: (row: AlarmRow) => 'common.enum.alarm-severity.' + row.severity,
          getColor: (row: AlarmRow) => this._alarmService.mapAlarmSeverityClass(row.severity),
        },
      ],
      title: {
        key: 'message',
      },
      sections: [
        {
          key: 'created_at',
          title: 'common.table.created-at.label',
          isValueTranslated: true,
          overrideValue: (row: AlarmRow) => formatTimeAgoTranslation(this._translateService, row.created_at),
        },
      ],
    },
    showRows: 25,
    rowsPerPageOptions: [
      25,
      50,
      100,
    ],
    pagination: true,
    scrollHeight: '40vh',
    emptyMessage: 'common.table.empty-message.alarm',
    actions: [
      {
        icon: 'visibility',
        action: (row: AlarmRow) => {
          const alarm: AlarmDto | undefined =
            this._device?.alarms?.find((alarmItem: AlarmDto) =>
              alarmItem.id === row.id,
            );

          if (!alarm) {
            return;
          }

          this._logService.sendLog({
            target: LoggingTargetEnum.ALARM,
            action: LoggingActionEnum.VIEW,
            target_id: alarm.id,
          });

          this.selectedAlarm = alarm;
          this.isAlarmDetailOpen = true;
        },
      },
    ],
  };

  @Input()
  set device(value: DeviceDto | null) {
    this._device = value;

    if (!this._device) return;

    this._alarmService.getAll({
      limit: -1,
      offset: 0,
      device_id: this._device.id,
    })
      .subscribe({
        next: (alarmResponse: AlarmFindAndCountResponseDto) => {

          if (this._device)
            this._device.alarms = alarmResponse.data;

          this.alarmTableRows = alarmResponse.data.map((alarm: AlarmDto) => {
            return {
              id: alarm.id,
              status: alarm.status,
              severity: alarm.severity,
              message: alarm.title,
              created_at: alarm.created_at,
            };
          });

          this.alarmTableConfig.totalRecords = alarmResponse.rowCount;
        },
      });
  }

  public get alarmDetailDialogName(): string {
    if (!this.selectedAlarm) return '';
    return 'common.enum.alarm-type.' + this.selectedAlarm.alarm_type;
  }

  public readonly closeAlarmDetailDialog = (): void => {
    this.isAlarmDetailOpen = false;
    this.isAlarmStatusUpdateOpen = false;
    this.selectedAlarm = null;
    this.alarmStatusLoading = false;
  };

  public onAlarmStatusAction(alarm: AlarmDto | null): void {
    if (!alarm || this.alarmStatusLoading) {
      return;
    }

    this.selectedAlarm = alarm;
    this.isAlarmStatusUpdateOpen = true;
  }
}
